import React from 'react';
import { Row, Col, Typography, Space } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHostProfile } from '../../contextProviders/HostProfileProvider';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Text } = Typography;

interface FooterLink {
  labelKey: string;
  to: string;
}

const SUPPORT_LINKS: FooterLink[] = [
  { labelKey: TRANSLATIONS.FOOTER_LINK_HELP_CENTER, to: '/info/help-center' },
  { labelKey: TRANSLATIONS.FOOTER_LINK_SAFETY, to: '/info/safety' },
  { labelKey: TRANSLATIONS.FOOTER_LINK_CANCELLATION_OPTIONS, to: '/info/cancellation-options' },
  { labelKey: TRANSLATIONS.FOOTER_LINK_CONTACT, to: '/info/contact' },
];

const HOSTING_LINKS: FooterLink[] = [
  { labelKey: TRANSLATIONS.FOOTER_LINK_HOST_RESOURCES, to: '/info/host-resources' },
  { labelKey: TRANSLATIONS.FOOTER_LINK_HOST_COMMUNITY, to: '/info/host-community' },
  { labelKey: TRANSLATIONS.FOOTER_LINK_HOST_LIABILITY, to: '/info/host-liability' },
];

const COMPANY_LINKS: FooterLink[] = [
  { labelKey: TRANSLATIONS.FOOTER_LINK_ABOUT, to: '/info/about' },
  { labelKey: TRANSLATIONS.FOOTER_LINK_HOW_IT_WORKS, to: '/info/how-it-works' },
  { labelKey: TRANSLATIONS.FOOTER_LINK_CAREERS, to: '/info/careers' },
  { labelKey: TRANSLATIONS.FOOTER_LINK_PRESS, to: '/info/press' },
];

/**
 * Shared footer content for both AppFooter (signed-in) and LandingLayout
 * (signed-out) -- same links regardless of auth state, same as any
 * marketplace's footer. Every link below is a placeholder InfoPage (see
 * constants/infoPages.ts) with generic template copy, except "Publica tu
 * espacio" which reuses AppHeader's own routing decision, and the bottom
 * bar's Términos/Privacidad, which are deliberately left untouched --
 * real legal content is out of scope here.
 */
const SiteFooter: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasHostProfile } = useHostProfile();

  const goToListYourSpace = () => navigate(hasHostProfile ? '/heavy-duty-machinery/new' : '/host/onboarding');

  const renderColumn = (titleKey: string, links: FooterLink[]) => (
    <Col xs={24} sm={8}>
      <Text strong style={{ display: 'block', marginBottom: 12 }}>
        {translate(t, titleKey)}
      </Text>
      <Space direction="vertical" size={16}>
        {links.map((link) => (
          <Link key={link.to} to={link.to} style={{ color: 'inherit', opacity: 0.85 }}>
            {translate(t, link.labelKey)}
          </Link>
        ))}
      </Space>
    </Col>
  );

  return (
    <div style={{ textAlign: 'center', maxWidth: 960, margin: '0 auto' }}>
      <Row gutter={[24, 24]} justify="center">
        {renderColumn(TRANSLATIONS.FOOTER_SECTION_SUPPORT, SUPPORT_LINKS)}
        <Col xs={24} sm={8}>
          <Text strong style={{ display: 'block', marginBottom: 12 }}>
            {translate(t, TRANSLATIONS.FOOTER_SECTION_HOSTING)}
          </Text>
          <Space direction="vertical" size={16}>
            <span
              onClick={goToListYourSpace}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') goToListYourSpace();
              }}
              style={{ color: 'inherit', opacity: 0.85, cursor: 'pointer' }}
            >
              {translate(t, TRANSLATIONS.FOOTER_LINK_BECOME_HOST)}
            </span>
            {HOSTING_LINKS.map((link) => (
              <Link key={link.to} to={link.to} style={{ color: 'inherit', opacity: 0.85 }}>
                {translate(t, link.labelKey)}
              </Link>
            ))}
          </Space>
        </Col>
        {renderColumn(TRANSLATIONS.FOOTER_SECTION_COMPANY, COMPANY_LINKS)}
      </Row>

      <div style={{ textAlign: 'center', marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          © {new Date().getFullYear()} · {translate(t, TRANSLATIONS.APP_FOOTER_TERMS)} · {translate(t, TRANSLATIONS.APP_FOOTER_PRIVACY)}
        </Text>
      </div>
    </div>
  );
};

export default SiteFooter;
